import styles from "../../../../assets/scss/common/modal/loginModal.module.scss";
import React, { useCallback, useRef, useState } from "react";
import { ErrorType } from "../../../../types/error";
import {useAuth, useModal, useUserAgent} from "../../../../hooks/hooks";
import { LoginStep } from "../LoginModal";
import { Timer } from "./Timer";
import {Trans, useTranslation} from "react-i18next";

interface Props {
  step: LoginStep;
  email: string;
  phoneNumber: string;
  changeLoginStep: (_: LoginStep) => void;
  setIncorrectCodeCount: (_: number) => void;
  setIsBackAfterSendingCode: (_: boolean) => void;
}

export function CodeStep({
  step,
  email,
  phoneNumber,
  changeLoginStep,
  setIncorrectCodeCount,
  setIsBackAfterSendingCode,
}: Props) {
  const { t } = useTranslation("signupModal");
  const LOGIN_TRY_COUNT = 5;
  const LOGIN_BLOCK_SEC = 5 * 60;
  const codeFormRef = useRef<HTMLFormElement>(null);
  const { signIn } = useAuth();
  const { closeModal, openAlertModal } = useModal();

  const [loginCode, setLoginCode] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [canLogin, setCanLogin] = useState(true);
  const {isMobile} = useUserAgent();

  const login = useCallback(
      async (emailAddr: string, code: string) => {
        signIn(
            { email: emailAddr, code },
            () => {
              closeModal();
              changeLoginStep(LoginStep.EMAIL);
            },
            (e: ErrorType) => {
              const serverErrorCode = e.errorCode.httpCode;

              switch (serverErrorCode) {
                case 461:
                  setAlertMessage(t("warning.authenticate.alreadyUse"));
                  setCanLogin(false);
                  break;
                case 462:
                  openAlertModal({
                    title: t("modal.authenticate.title"),
                    desc: t("modal.authenticate.waiting"),
                  });
                  break;
                case 463:
                  setAlertMessage(t("warning.authenticate.timeExceed"));
                  setCanLogin(false);
                  break;
                case 464:
                  setIncorrectCodeCount(LOGIN_TRY_COUNT);
                  setAlertMessage(t("warning.authenticate.failExceed"));
                  break;
                case 465:
                  const incorrectCount = e?.data?.incorrect_count ?? 0;
                  setIncorrectCodeCount(incorrectCount);

                  let message = `인증코드를 잘못입력하셨습니다. (${
                      incorrectCount ? `${incorrectCount}/${LOGIN_TRY_COUNT}회)` : ""
                  }`;
                  if (incorrectCount >= LOGIN_TRY_COUNT) {
                    message += `\n아래 취소버튼을 통해 다시 시도해주세요.`;
                    setCanLogin(false);
                  }

                  setAlertMessage(message);
                  break;
                default:
                  openAlertModal(null);
                  break;
              }
            }
        );
      },
      [changeLoginStep, closeModal, openAlertModal, setIncorrectCodeCount, signIn, t]
  );

  const onClickLoginBtn = () => {
    const trimmedCode = loginCode.trim();
    if (trimmedCode === "") {
      setAlertMessage(t("warning.authenticate.empty"));
      return;
    }

    const codeRegExp = /^[0-9|a-z|A-Z]{6}$/;
    if (!codeRegExp.test(trimmedCode)) {
      setAlertMessage(t("warning.authenticate.wrongCode"));
      return;
    }

    login(email.trim(), trimmedCode).then();
  };

  const onClickCancelBtn = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setAlertMessage("");
        changeLoginStep(LoginStep.EMAIL);
        setIsBackAfterSendingCode(true);
      },
      [changeLoginStep, setIsBackAfterSendingCode]
  );

  const doAtTheEnd = useCallback(() => {
    setAlertMessage(t("warning.authenticate.timeExceed"));
    setCanLogin(false);
  }, [t]);

  return (
      <div className={styles.textDesc}>
        <div className={styles.desc}>
          {step === LoginStep.EMAIL_CODE && (
              <>
                <span className={styles.accent}>{email}</span>
                <br />{t("authenticate.mail")}
              </>
          )}
          {step === LoginStep.PHONE_NUMBER_CODE && (
              <>
                <Trans t={t} i18nKey={isMobile ? "authenticate.cellphoneMo" :"authenticate.cellphone"}
                       values={{phoneNumber: phoneNumber}}
                       components={{accent: <span className={styles.accent} />,br : <br />}}
                />
              </>
          )}
        </div>
        <form className={styles.emailForm} onSubmit={(e) => e.preventDefault()} ref={codeFormRef}>
          <input
              type="tel"
              className={styles.input}
              value={loginCode}
              onInvalid={(e) => e.preventDefault()}
              onChange={(e) => setLoginCode(e.target.value.toUpperCase())}
              placeholder={t("authenticate.placeholder")}
          />
          {canLogin ? (
              <Timer initialMilliseconds={LOGIN_BLOCK_SEC * 1000} styleClassName={styles.timer} doAtTheEnd={doAtTheEnd} />
          ) : (
              <></>
          )}
          {alertMessage ? <span className={styles.invalidEmailAddr}>{alertMessage}</span> : <></>}
          <div className={`${styles.buttonsWrapper} ${styles.withMarginTop}`}>
            <button type="button" className={styles.outlineButton} onClick={onClickCancelBtn}>
              {t("btn.cancel")}
            </button>
            {canLogin ? (
                <button type="button" className={styles.primaryButton} onClick={onClickLoginBtn}>
                  {t("btn.login")}
                </button>
            ) : (
                <></>
            )}
          </div>
        </form>
      </div>
  );
}
